import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    inject,
    input,
    Output,
    signal,
    ViewChild,
} from '@angular/core';
import { SharedUiModule } from '@pf/shared-ui';
import { EquipmentDto, EquipmentFacade } from '@pf/platform-wms/domain';
import { first, Subject, tap } from 'rxjs';
import { EquipmentStatus, ZoneType } from '@control-tower/platform-wms';
import { FormControl } from '@angular/forms';
import {
    BarcodeScanEvent,
    CpwScanInputComponent,
} from './cpw-scan-input.component';
import { canHandleGroup, EquipmentGroup, isHandlingLtl } from '@cpw/domain';
import { ProfileProvider } from '@pf/shared-common';
import { UserPermissionCheckService } from '@pf/shared-services';
import { toSignal } from '@angular/core/rxjs-interop';
import { startCase } from 'lodash-es';

export type EquipmentScanEvent = {
    equipment: EquipmentDto;
    equipmentGroup: EquipmentGroup | undefined;
    processed: Subject<boolean>;
};

@Component({
    standalone: true,
    selector: 'cpw-equipment-scan',
    imports: [SharedUiModule, CpwScanInputComponent],
    templateUrl: './cpw-equipment-scan.component.html',
    styleUrl: './cpw-equipment-scan.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwEquipmentScanComponent implements AfterViewInit {
    private readonly profileProvider = inject(ProfileProvider);
    private readonly userPermissionCheckService = inject(
        UserPermissionCheckService
    );
    readonly equipmentFacade = inject(EquipmentFacade);

    scanError = signal('');
    processingEquipment = signal(false);

    private readonly isForkliftOperator = toSignal(
        this.userPermissionCheckService.checkPermission$(
            'WMS:ForkliftOperator'
        ),
        { initialValue: false }
    );

    @ViewChild(CpwScanInputComponent) scanInput!: CpwScanInputComponent;

    equipmentBarcodeControl = new FormControl<string>('');
    autoFocus = input<boolean>(false);
    disabled = input<boolean>(false);
    equipmentGroup = input<EquipmentGroup>();
    validationRule = input<'Available' | 'Staged'>('Available');
    stagingZoneType = input<ZoneType | null>(null);
    @Output() equipmentScanned = new EventEmitter<EquipmentScanEvent>();

    label = computed(() => {
        const group = this.equipmentGroup();
        if (!group) {
            return 'Scan or enter equipment barcode';
        }
        return group === 'Parts'
            ? 'Scan or enter cart barcode'
            : 'Scan or enter forklift/pallet jack barcode';
    });

    ngAfterViewInit(): void {
        if (this.autoFocus()) {
            this.focus(true);
        }
    }

    focus(selectText: boolean) {
        this.scanInput.focus(selectText);
    }

    equipmentBarcodeScanned($event: BarcodeScanEvent) {
        this.checkEquipmentLookup($event);
    }

    private checkEquipmentLookup(event: BarcodeScanEvent) {
        const equipment = event.entity as EquipmentDto;
        if (!this.isEquipmentValid(equipment)) {
            this.scanError.set(
                `Equipment is ${startCase(equipment.status).toLowerCase()} and cannot be used for this action.`
            );
            event.processed.next(false);
            return;
        }

        const group = this.equipmentGroup();
        if (group && !canHandleGroup(group, equipment.equipmentType)) {
            this.scanError.set(
                `Incorrect equipment. A ${equipment.equipmentType.name} was scanned.`
            );
            event.processed.next(false);
            return;
        }

        if (
            !this.isForkliftOperator() &&
            isHandlingLtl(equipment.equipmentType)
        ) {
            this.scanError.set(
                'You do not have permission to operate a forklift.'
            );
            event.processed.next(false);
            return;
        }

        this.scanError.set('');
        this.emitEquipmentScanned(equipment, event.processed);
    }

    private isEquipmentValid(equipment: EquipmentDto): boolean {
        const rule = this.validationRule();
        const isLtl = isHandlingLtl(equipment.equipmentType);
        const isStaged = isLtl || equipment.status === EquipmentStatus.Staged;
        const canBeAssignedToUser =
            !equipment.assignedToUser ||
            equipment.assignedToUser.id ===
                this.profileProvider.profile?.userId;

        if (rule === 'Available') {
            const isAvailable = equipment.status === EquipmentStatus.Available;
            const isInUseByCurrentUser =
                equipment.status === EquipmentStatus.InUse &&
                canBeAssignedToUser;
            return isAvailable || isInUseByCurrentUser || isStaged;
        } else {
            return isStaged && canBeAssignedToUser;
        }
    }

    private emitEquipmentScanned(
        equipment: EquipmentDto,
        processed: Subject<boolean>
    ) {
        this.processingEquipment.set(true);
        const processEquipmentSubject = new Subject<boolean>();
        processEquipmentSubject.pipe(
            first(),
            tap(() => this.processingEquipment.set(false)),
            tap(success => {
                if (!success) {
                    processed.next(false);
                    this.scanError.set('Failed to process scan. Try again.');
                }
                processed.next(true);
            })
        );
        this.equipmentScanned.emit({
            equipment: equipment,
            equipmentGroup: this.equipmentGroup(),
            processed: processEquipmentSubject,
        });
    }
}
